// src/NavBar.js
import * as React from 'react';
// Remove useNavigate if not used elsewhere
import { useLocation, Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';

// Define the pages shown in the NavBar
const pages = [
  { label: 'Home', path: '/landing' },
  { label: 'Dispatcher', path: '/dispatcher' }, // Will be disabled
  { label: 'Flight Breakdown', path: '/breakdown' }, // Main active page
  { label: 'Flights', path: '/flights' }, // Saved flights list
  { label: 'User Settings', path: '/user-settings' }, // Will be disabled
];

// List of labels for links to be disabled
const disabledPages = ['Dispatcher', 'User Settings'];

function NavBar() {
  const location = useLocation();
  const [anchorElNav, setAnchorElNav] = React.useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  // No handleLogout needed

  const isActiveRoute = (path) => location.pathname === path;

  // --- Helper function for Desktop Button Styles ---
  const getDesktopButtonStyles = (page, isDisabled) => {
    const active = isActiveRoute(page.path);
    let styles = {
      fontWeight: 600, color: '#fff', cursor: 'pointer',
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#f9b612' },
    };
    if (active) styles = { ...styles, color: '#f9b612', fontWeight: 700 };
    if (isDisabled) styles = {
      ...styles, color: 'rgba(255, 255, 255, 0.5)', fontWeight: 600, cursor: 'not-allowed',
      '&:hover': { backgroundColor: 'transparent', color: 'rgba(255, 255, 255, 0.5)' },
    };
    return styles;
  };

  // --- Helper function for Mobile Button Styles ---
  const getMobileButtonStyles = (page, isDisabled) => {
    const active = isActiveRoute(page.path);
    let styles = {
      fontWeight: 400, color: '#000', width: '100%', justifyContent: 'flex-start', cursor: 'pointer',
      '&:hover': { color: '#f9b612', backgroundColor: 'rgba(0, 0, 0, 0.04)' },
    };
    if (active) styles = { ...styles, color: '#f9b612', fontWeight: 700 };
    if (isDisabled) styles = {
      ...styles, color: 'grey', fontWeight: 400, cursor: 'not-allowed',
      '&:hover': { backgroundColor: 'transparent', color: 'grey' },
    };
    return styles;
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        top: '20px', left: '50%', transform: 'translateX(-50%)',
        width: '90%', maxWidth: '1200px', // Optional: constrain max width
        borderRadius: '8px', borderWidth: '0.5px', borderStyle: 'solid',
        borderColor: '#e2e2e2', backgroundColor: 'rgba(17, 24, 39, 0.8)',
        backdropFilter: 'blur(5px)', boxShadow: '0 3px 10px rgba(0,0,0,0.3)',
        zIndex: 1300,
      }}
    >
      <Toolbar disableGutters sx={{ px: 2 }}>
        {/* MOBILE: Hamburger Icon & Menu */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}> {/* Reduced margin */}
          <IconButton size="large" onClick={handleOpenNavMenu} color="inherit">
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={anchorElNav}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{ display: { xs: 'block', md: 'none' }, '& .MuiPaper-root': { marginTop: '8px' } }}
          >
            {pages.map((page) => {
              const isDisabled = disabledPages.includes(page.label); // Check against disabled list
              return (
                <MenuItem key={page.label} onClick={handleCloseNavMenu} disabled={isDisabled} sx={{ p: 0 }}>
                  <Button component={Link} to={page.path} disabled={isDisabled} sx={getMobileButtonStyles(page, isDisabled)}>
                    {page.label}
                  </Button>
                </MenuItem>
              );
            })}
          </Menu>
        </Box>

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <Box sx={{ ml: 1 }}>
            <img src="/FullLogo_Transparent_NoBuffer-2.png" alt="Denver Dispatch Logo" style={{ height: '40px' }} />
          </Box>
        </Box>

        {/* DESKTOP: Nav Links */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: { md: 2, lg: 3 } }}> {/* Adjusted gap */}
          {pages.map((page) => {
            const isDisabled = disabledPages.includes(page.label); // Check against disabled list
            return (
              <Button key={page.label} component={Link} to={page.path} disabled={isDisabled} sx={getDesktopButtonStyles(page, isDisabled)}>
                {page.label}
              </Button>
            );
          })}
        </Box>

        {/* RIGHT SIDE: Empty Box to balance Toolbar (if needed) or add other icons */}
        <Box sx={{ display: 'flex', width: { xs: 'auto', md: '64px' } }} /> {/* Placeholder to balance */}

      </Toolbar>
    </AppBar>
  );
}

export default NavBar;
